import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useGetWorkflowDetails } from "@features/ux/components/workflow/hooks";
import { Badge } from "@shared/ui/badge";
import React from "react";
import { useParams } from "react-router-dom";

const WorkFlowDetails = ({ workflowDetail }: { workflowDetail: any }) => {
  const { id, name } = useParams();

  // This data would come from your create workflow form/API
  const workflowData = {
    workflowName: workflowDetail?.result?.name || "-",
    description: workflowDetail?.result?.description,
    status: workflowDetail?.result?.status,
    audienceType: workflowDetail?.result?.audienceTypeDisplay,
    actionType: workflowDetail?.result?.actionTypeDisplay, // or 'Time Based' or 'Event Based'
    recipientList: workflowDetail?.result?.recipientListDisplay || "N/A",
    recipients: workflowDetail?.result?.recipients || 0,
    scheduleType: workflowDetail?.result?.scheduleType, // 'Schedule for later' or 'Recurring'
    scheduleDate: workflowDetail?.result?.scheduleDate,
    scheduleTime: workflowDetail?.result?.scheduleTime,
    frequency: workflowDetail?.result?.frequencyDisplay,
    startDate: workflowDetail?.result?.startDate,
    endDate: workflowDetail?.result?.endDate,
    time: workflowDetail?.result?.scheduleTime,
    eventTrigger: workflowDetail?.result?.triggerTypeDisplay,
    sequencing: workflowDetail?.result?.messageSequence?.length,
    createdBy: workflowDetail?.result?.createdUserRemoteName,
    createdOn: workflowDetail?.result?.createdDate,
    updatedBy: workflowDetail?.result?.lastModifiedUserRemoteName,
    updatedOn: workflowDetail?.result?.lastModifiedDate,
  };

  // Merged workflow details with all action-specific fields
  const workflowDetails = [
    {
      label: "Description",
      value: workflowData.description,
    },
    {
      label: "Audience Type",
      value: workflowData.audienceType,
    },
    {
      label: "Action Type",
      value: workflowData.actionType,
    },
  ];

  // Add conditional fields based on action type
  if (
    workflowData.actionType === "Immediate Action" ||
    workflowData.actionType === "Time-Based Action"
  ) {
    workflowDetails.push(
      {
        label: "Recipient List",
        value: workflowData.recipientList,
      },
      {
        label: "Recipients",
        value: workflowData.recipients,
      }
    );
  }

  if (workflowData.actionType === "Time-Based Action") {
    workflowDetails.push({
      label: "Schedule Type",
      value: workflowData.scheduleType,
    });

    if (workflowData.scheduleType === "schedule_later") {
      workflowDetails.push(
        {
          label: "Schedule Date",
          value: workflowData.scheduleDate,
        },
        {
          label: "Schedule Time",
          value: workflowData.scheduleTime,
        }
      );
    }

    if (workflowData.scheduleType === "recurring") {
      workflowDetails.push(
        {
          label: "Frequency",
          value: workflowData.frequency,
        },
        {
          label: "Start Date",
          value: workflowData.startDate,
        },
        {
          label: "End Date",
          value: workflowData.endDate,
        },
        {
          label: "Time",
          value: workflowData.time,
        }
      );
    }
  }

  if (workflowData.actionType === "Event-Based Action") {
    workflowDetails.push({
      label: "Event Trigger",
      value: workflowData.eventTrigger,
    });
  }

  workflowDetails.push({
    label: "Sequencing",
    value: workflowData.sequencing,
  });

  const creationDetails = [
    {
      label: "Created By",
      value: workflowData.createdBy,
    },
    {
      label: "Created On",
      value: workflowData.createdOn,
    },
    {
      label: "Updated By",
      value: workflowData.updatedBy,
    },
    {
      label: "Last Updated",
      value: workflowData.updatedOn,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-md font-semibold">{workflowData.workflowName}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive">{workflowData.status}</Badge>
          <Badge variant="outline">{workflowData.actionType}</Badge>
        </div>
      </div>
      <Badge variant="secondary">{workflowData.audienceType}</Badge>

      <div className="mt-4">
        <div className="border-b-2 border-gray-200"></div>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold text-foreground">
          Workflow Details
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 mt-3">
        {workflowDetails.map((item) => (
          <ValueLabel key={item.label} label={item.label} value={item.value} />
        ))}
      </div>

      <div className="mt-4">
        <div className="border-b-2 border-gray-200"></div>
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-semibold text-foreground">
          Creation Details
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 mt-3">
        {creationDetails.map((item) => (
          <ValueLabel key={item.label} label={item.label} value={item.value} />
        ))}
      </div>
    </div>
  );
};

export default WorkFlowDetails;